import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const ScoringMethodologyModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  const scoringCategories = [
    {
      id: 'reviews',
      name: 'Reviews',
      icon: 'Star',
      description: 'Vendor star ratings and customer feedback',
      gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)',
      scale: [
        { range: '5.0 stars', score: '100', description: 'Perfect rating' },
        { range: '4.7-4.9 stars', score: '94-98', description: 'Excellent reputation' },
        { range: '4.3-4.6 stars', score: '86-93', description: 'Good reputation' },
        { range: '3.5-4.2 stars', score: '70-85', description: 'Fair reputation' },
        { range: 'Below 3.5', score: '<70', description: 'Poor reputation' }
      ],
      notes: [
        'Review count affects confidence: 500+ reviews = full score, <10 reviews = 15% penalty',
        'More reviews provide higher reliability in the rating'
      ]
    },
    {
      id: 'price',
      name: 'Price/Budget',
      icon: 'DollarSign',
      description: 'Cost-effectiveness and value',
      gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)',
      scale: [
        { range: 'Lowest price', score: '100', description: 'Best value' },
        { range: 'Mid-range', score: '75-85', description: 'Competitive pricing' },
        { range: 'Highest price', score: '50', description: 'Premium pricing' }
      ],
      notes: [
        'Relative scoring: compares all vendor prices',
        'Lower price = higher score (assuming similar quality)'
      ]
    },
    {
      id: 'processor',
      name: 'Processor/Chip',
      icon: 'Cpu',
      description: 'CPU performance and generation',
      gradient: 'linear-gradient(135deg, #E0528A, #c026d3)',
      scale: [
        { range: 'Apple M4, Intel i9, AMD Ryzen 9', score: '100', description: 'Top-tier performance' },
        { range: 'Apple M3 Pro/Max, Intel i7, AMD Ryzen 7', score: '95-98', description: 'High performance' },
        { range: 'Apple M3, Intel i5, AMD Ryzen 5', score: '85-90', description: 'Mid-range performance' },
        { range: 'Intel i3, AMD Ryzen 3', score: '70', description: 'Entry-level performance' },
        { range: 'Older generations', score: '<70', description: 'Legacy performance' }
      ],
      notes: [
        'Core count considered: 16+ cores = 100, 8 cores = 85, 4 cores = 65',
        'Latest generation chips score higher'
      ]
    },
    {
      id: 'memory',
      name: 'Memory (RAM)',
      icon: 'HardDrive',
      description: 'System memory capacity',
      gradient: 'linear-gradient(135deg, #f59e0b, #F58A54)',
      scale: [
        { range: '64GB+', score: '100', description: 'Professional workstation' },
        { range: '32GB', score: '95', description: 'Power user' },
        { range: '16-24GB', score: '85-90', description: 'Standard performance' },
        { range: '8-12GB', score: '65-75', description: 'Basic usage' },
        { range: '4GB or less', score: '<50', description: 'Minimal usage' }
      ],
      notes: [
        'More RAM = better multitasking and performance',
        'Modern applications require 16GB minimum for optimal performance'
      ]
    },
    {
      id: 'storage',
      name: 'Storage Capacity',
      icon: 'Database',
      description: 'SSD/HDD storage space',
      gradient: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
      scale: [
        { range: '2TB+ SSD', score: '100', description: 'Massive storage' },
        { range: '1TB SSD', score: '95', description: 'Ample storage' },
        { range: '512GB SSD', score: '85', description: 'Standard storage' },
        { range: '256GB SSD', score: '70', description: 'Basic storage' },
        { range: 'HDD (any size)', score: '-15 penalty', description: 'Slower performance' }
      ],
      notes: [
        'SSD provides +10 bonus points for speed',
        'HDD receives -15 penalty for slower performance'
      ]
    },
    {
      id: 'delivery',
      name: 'Delivery Time',
      icon: 'Truck',
      description: 'Shipping speed and availability',
      gradient: 'linear-gradient(135deg, #8b5cf6, #E0528A)',
      scale: [
        { range: 'Same day', score: '100', description: 'Immediate availability' },
        { range: '1-2 days', score: '95-98', description: 'Express delivery' },
        { range: '3-5 days', score: '85-90', description: 'Standard shipping' },
        { range: '1-2 weeks', score: '65-75', description: 'Delayed shipping' },
        { range: '1 month+', score: '<50', description: 'Extended wait' }
      ],
      notes: [
        'Faster delivery = higher convenience score',
        'Consider urgency of your project needs'
      ]
    },
    {
      id: 'warranty',
      name: 'Warranty Support',
      icon: 'Shield',
      description: 'Coverage and support duration',
      gradient: 'linear-gradient(135deg, #10b981, #06b6d4)',
      scale: [
        { range: '5+ years', score: '100', description: 'Extended protection' },
        { range: '3-4 years', score: '95', description: 'Strong coverage' },
        { range: '2 years', score: '85', description: 'Standard coverage' },
        { range: '1 year', score: '75', description: 'Basic coverage' },
        { range: '6 months or less', score: '<60', description: 'Limited coverage' }
      ],
      notes: [
        'Extended warranty options add +10 bonus points',
        'Longer coverage = better long-term protection'
      ]
    },
    {
      id: 'interestRate',
      name: 'Interest Rate',
      icon: 'Percent',
      description: 'Loan/mortgage rate comparison',
      gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)',
      scale: [
        { range: 'Lowest rate', score: '100', description: 'Best financing' },
        { range: 'Mid-range rate', score: '75-85', description: 'Competitive rate' },
        { range: 'Highest rate', score: '50', description: 'Premium rate' }
      ],
      notes: [
        'Relative scoring: compares all vendor rates',
        'Lower interest rate = higher score and savings'
      ]
    },
    {
      id: 'fuelEfficiency',
      name: 'Fuel Efficiency',
      icon: 'Fuel',
      description: 'MPG rating for vehicles',
      gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)',
      scale: [
        { range: '50+ MPG', score: '100', description: 'Hybrid/Electric level' },
        { range: '35-49 MPG', score: '90-95', description: 'Excellent efficiency' },
        { range: '25-34 MPG', score: '75-85', description: 'Good efficiency' },
        { range: '15-24 MPG', score: '50-65', description: 'Average efficiency' },
        { range: 'Below 15 MPG', score: '<50', description: 'Poor efficiency' }
      ],
      notes: [
        'Higher MPG = lower fuel costs',
        'Consider your typical driving patterns'
      ]
    },
    {
      id: 'safety',
      name: 'Safety Rating',
      icon: 'Shield',
      description: 'NHTSA/IIHS safety scores',
      gradient: 'linear-gradient(135deg, #E0528A, #c026d3)',
      scale: [
        { range: '5 stars / Top Safety Pick+', score: '100', description: 'Highest safety' },
        { range: '4 stars / Top Safety Pick', score: '85', description: 'Strong safety' },
        { range: '3 stars / Good', score: '70', description: 'Adequate safety' },
        { range: '2 stars / Acceptable', score: '55', description: 'Basic safety' },
        { range: '1 star', score: '<40', description: 'Poor safety' }
      ],
      notes: [
        'Based on NHTSA 5-star or IIHS ratings',
        'Higher ratings = better crash protection'
      ]
    },
    {
      id: 'battery',
      name: 'Battery Life',
      icon: 'Battery',
      description: 'Usage duration per charge',
      gradient: 'linear-gradient(135deg, #f59e0b, #F58A54)',
      scale: [
        { range: '20+ hours', score: '100', description: 'All-day+ usage' },
        { range: '12-19 hours', score: '90-95', description: 'Full workday' },
        { range: '8-11 hours', score: '75-85', description: 'Standard usage' },
        { range: '4-7 hours', score: '50-65', description: 'Limited usage' },
        { range: 'Below 4 hours', score: '<50', description: 'Frequent charging' }
      ],
      notes: [
        'Longer battery life = better portability',
        'Consider your typical usage patterns'
      ]
    },
    {
      id: 'camera',
      name: 'Camera Quality',
      icon: 'Camera',
      description: 'Photo/video capabilities',
      gradient: 'linear-gradient(135deg, #8b5cf6, #E0528A)',
      scale: [
        { range: '108+ MP', score: '100', description: 'Professional grade' },
        { range: '48-107 MP', score: '90-95', description: 'High quality' },
        { range: '24-47 MP', score: '85', description: 'Very good' },
        { range: '12-23 MP', score: '75', description: 'Good quality' },
        { range: 'Below 12 MP', score: '<65', description: 'Basic quality' }
      ],
      notes: [
        'Optical zoom adds +5 bonus points',
        'Night mode adds +5 bonus points',
        "Megapixels aren\'t everything - sensor quality matters"
      ]
    }
  ];

  const scoreRanges = [
    { range: '95-100', label: 'Excellent', desc: 'Best in class, top-tier performance', bg: 'linear-gradient(135deg, #F58A54, #F06F6B)', text: 'white' },
    { range: '85-94', label: 'Good', desc: 'Above average, strong performance', bg: 'linear-gradient(135deg, #F06F6B, #E0528A)', text: 'white' },
    { range: '70-84', label: 'Fair', desc: 'Meets expectations, acceptable', bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
    { range: '50-69', label: 'Poor', desc: 'Below expectations, needs improvement', bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' },
    { range: '0-49', label: 'Very Poor', desc: 'Significantly lacking, not recommended', bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        style={{ border: '1.5px solid #fde8d8' }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 50%, #E0528A 100%)' }}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
              <Icon name="BarChart2" size={22} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Scoring Methodology Guide</h2>
              <p className="text-white text-opacity-80 text-xs mt-0.5">How we calculate vendor scores</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b px-6" style={{ borderColor: '#fde8d8', background: '#fff8f5' }}>
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'overview' ?'border-[#F58A54] text-[#F58A54]' :'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-3 px-5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'metrics' ?'border-[#F58A54] text-[#F58A54]' :'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              Metric Details
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 148px)', background: '#fffaf8' }}>
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              {/* Scoring Scale */}
              <div
                className="rounded-2xl p-5"
                style={{ background: 'white', border: '1.5px solid #fde8d8' }}
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    <Icon name="Award" size={16} color="white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">Universal Scoring Scale (0–100)</h3>
                </div>
                <div className="space-y-2">
                  {scoreRanges?.map((s, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <div
                        className="w-20 text-center px-3 py-1 rounded-full text-xs font-bold flex-shrink-0"
                        style={{
                          background: s?.bg,
                          color: s?.text,
                          border: s?.border ? `1px solid ${s?.border}` : 'none',
                        }}
                      >
                        {s?.range}
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-900 text-sm">{s?.label}</span>
                        <span className="text-gray-500 text-sm ml-2">— {s?.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <div
                className="rounded-2xl p-5"
                style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #fff0f5 100%)', border: '1.5px solid #fde8d8' }}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}
                  >
                    <Icon name="Lightbulb" size={16} color="white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">How Scoring Works</h3>
                </div>
                <ul className="space-y-2">
                  {[
                    'Each metric is evaluated on a standardized 0-100 scale',
                    'Scores are calculated using industry-standard benchmarks and relative comparisons',
                    'Your custom weights determine how much each metric affects the overall recommendation',
                    'Overall score = Sum of (Metric Score × Weight Percentage) for all metrics',
                  ]?.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-gray-700">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                      >
                        <Icon name="Check" size={11} color="white" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Example Calculation */}
              <div
                className="rounded-2xl p-5"
                style={{ background: 'white', border: '1.5px solid #fde8d8' }}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #E0528A, #c026d3)' }}
                  >
                    <Icon name="Calculator" size={16} color="white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">Example Calculation</h3>
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-3">Laptop Comparison:</div>
                <div className="space-y-2">
                  {[
                    { label: 'Processor', score: '85%', weight: '30%', points: '25.5' },
                    { label: 'Memory', score: '100%', weight: '25%', points: '25.0' },
                    { label: 'Storage', score: '85%', weight: '20%', points: '17.0' },
                    { label: 'Price', score: '100%', weight: '15%', points: '15.0' },
                    { label: 'Reviews', score: '88%', weight: '10%', points: '8.8' },
                  ]?.map((row, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 w-24">{row?.label}</span>
                      <span className="text-gray-500">{row?.score} × {row?.weight}</span>
                      <span
                        className="font-semibold px-2 py-0.5 rounded-full text-xs"
                        style={{ background: '#fff5f0', color: '#F58A54' }}
                      >
                        {row?.points} pts
                      </span>
                    </div>
                  ))}
                  <div
                    className="flex items-center justify-between text-sm font-bold pt-2 mt-1 rounded-xl px-3 py-2"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)', color: 'white' }}
                  >
                    <span>Overall Score</span>
                    <span>91.3% — Excellent 🎉</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'metrics' && (
            <div className="p-6 space-y-4">
              {scoringCategories?.map((category) => (
                <div
                  key={category?.id}
                  className="rounded-2xl p-5 hover:shadow-md transition-shadow"
                  style={{ background: 'white', border: '1.5px solid #fde8d8' }}
                >
                  <div className="flex items-start space-x-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: category?.gradient }}
                    >
                      <Icon name={category?.icon} size={20} color="white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{category?.name}</h4>
                      <p className="text-sm text-gray-500">{category?.description}</p>
                    </div>
                  </div>

                  {/* Scoring Scale */}
                  <div className="ml-13 space-y-2 pl-1">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Scoring Scale</div>
                    {category?.scale?.map((item, index) => (
                      <div key={index} className="flex items-center space-x-3 text-sm">
                        <div className="w-36 flex-shrink-0 text-gray-800 font-medium text-xs">{item?.range}</div>
                        <div className="w-16 flex-shrink-0">
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ background: '#fff5f0', color: '#F58A54', border: '1px solid #fde8d8' }}
                          >
                            {item?.score}
                          </span>
                        </div>
                        <div className="flex-1 text-gray-500 text-xs">{item?.description}</div>
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  {category?.notes && category?.notes?.length > 0 && (
                    <div
                      className="mt-3 rounded-xl p-3"
                      style={{ background: '#fff8f5', border: '1px solid #fde8d8' }}
                    >
                      <div className="flex items-center space-x-1 mb-1">
                        <Icon name="Info" size={12} color="#F58A54" />
                        <span className="text-xs font-semibold" style={{ color: '#F58A54' }}>Important Notes</span>
                      </div>
                      <ul className="space-y-1">
                        {category?.notes?.map((note, index) => (
                          <li key={index} className="text-xs text-gray-600 flex items-start">
                            <span className="mr-1 text-[#F58A54]">•</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 flex justify-end"
          style={{ borderTop: '1.5px solid #fde8d8', background: '#fff8f5' }}
        >
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
          >
            Got It!
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoringMethodologyModal;